import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaCheck, FaTrash, FaUndo } from 'react-icons/fa';
import { Shift } from '../../shifts/services/ShiftService';
import { CrmTask } from '../../sales/services/CrmTaskService';
import { LaneTeam } from '../services/PlantafelService';
import PlantafelConflictModal from './PlantafelConflictModal';
import PlantafelService from '../services/PlantafelService';
import styles from './PlantafelSidePanel.module.css';

import { useSidePanelState } from '../hooks/useSidePanelState';
import { SidePanelTaskInfo } from './SidePanelTaskInfo';
import { SidePanelTimeEdit } from './SidePanelTimeEdit';
import { SidePanelAttachments } from './SidePanelAttachments';
import { SidePanelChecklist } from './SidePanelChecklist';
import { SidePanelComments } from './SidePanelComments';

interface PlantafelSidePanelProps {
  shift: Shift | null;
  task: CrmTask | null;
  allTeams?: LaneTeam[];
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

export default function PlantafelSidePanel({
  shift: shiftProp, task, allTeams = [], onClose, onRefresh,
}: PlantafelSidePanelProps): React.JSX.Element {
  const navigate = useNavigate();
  const s = useSidePanelState({ shiftProp, task, allTeams, onClose, onRefresh });

  return (
    <div data-testid="plantafel-sidepanel" className={styles.panelContainer}>
      {/* Hidden global file input for subtask attachments */}
      <input
        ref={s.subtaskFileInputRef}
        type="file"
        onChange={(e) => {
          const files = e.target.files;
          if (files && files.length > 0) s.setSubtaskPendingFiles(prev => [...prev, ...Array.from(files)]);
          e.target.value = '';
        }}
        className={styles.hiddenFileInput}
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
      />

      {/* Header */}
      <div className={styles.panelHeader}>
        <div>
          <span className={styles.headerLabel}>
            {(task || s.shift?.is_job_shift) ? 'Job Details' : 'Schicht Details'}
          </span>
          {task?.title && <h3 className={styles.headerTitle}>{task.title}</h3>}
        </div>
        <button onClick={onClose} className={styles.closeButton}>
          <FaTimes size={16} />
        </button>
      </div>

      {/* Content */}
      <div className={styles.contentScroll}>
        <SidePanelTaskInfo
          task={task} shift={s.shift} jobDetail={s.jobDetail} projectId={s.projectId}
          statusColors={s.statusColors} priorityColors={s.priorityColors}
          sites={s.sites} sitesLoading={s.sitesLoading} selectedSiteId={s.selectedSiteId} savingSite={s.savingSite}
          projectMilestones={s.projectMilestones} milestonesLoading={s.milestonesLoading} savingMilestone={s.savingMilestone}
          handleOpenPartner={() => {
            const partnerId = task?.partner || task?.partner_id || (s.shift as any)?.partner || (s.shift as any)?.partner_id || s.jobDetail?.partner_id;
            if (partnerId) navigate(`/contacts/${partnerId}`);
          }}
          handleOpenProject={() => {
            const pId = task?.project || task?.project_id || (s.shift as any)?.project || (s.shift as any)?.project_id || s.jobDetail?.project_id;
            if (pId) navigate(`/projects/${pId}`);
          }}
          handleOpenOffer={() => {
            const offerId = task?.offer || task?.offer_id || (s.shift as any)?.offer || (s.shift as any)?.offer_id || s.jobDetail?.offer_id;
            if (offerId) navigate(`/offers/${offerId}`);
          }}
          handleSaveSite={s.handleSaveSite} handleSaveMilestone={s.handleSaveMilestone} navigate={navigate}
        />

        {s.crmTaskId && (
          <SidePanelChecklist
            checklist={s.checklist} checklistLoading={s.checklistLoading}
            showChecklist={s.showChecklist} setShowChecklist={s.setShowChecklist}
            newChecklistItem={s.newChecklistItem} setNewChecklistItem={s.setNewChecklistItem}
            addingChecklistItem={s.addingChecklistItem} togglingItemId={s.togglingItemId}
            expandedSubtaskId={s.expandedSubtaskId} subtaskComments={s.subtaskComments}
            subtaskAttachments={s.subtaskAttachments} subtaskDataLoading={s.subtaskDataLoading}
            subtaskCommentText={s.subtaskCommentText} setSubtaskCommentText={s.setSubtaskCommentText}
            subtaskPendingFiles={s.subtaskPendingFiles} setSubtaskPendingFiles={s.setSubtaskPendingFiles}
            sendingSubtaskComment={s.sendingSubtaskComment} subtaskFileInputRef={s.subtaskFileInputRef}
            handleToggleChecklistItem={s.handleToggleChecklistItem}
            handleAddChecklistItem={s.handleAddChecklistItem}
            handleDeleteChecklistItem={s.handleDeleteChecklistItem}
            handleToggleSubtaskExpand={s.handleToggleSubtaskExpand}
            handleSendSubtaskComment={s.handleSendSubtaskComment}
            handleDeleteSubtaskComment={s.handleDeleteSubtaskComment}
            handleDeleteSubtaskAttachment={s.handleDeleteSubtaskAttachment}
          />
        )}

        <SidePanelTimeEdit
          shift={s.shift}
          editStartDate={s.editStartDate} editStartTime={s.editStartTime}
          editEndDate={s.editEndDate} editEndTime={s.editEndTime}
          setEditStartDate={s.setEditStartDate} setEditStartTime={s.setEditStartTime}
          setEditEndDate={s.setEditEndDate} setEditEndTime={s.setEditEndTime}
          hasTimeChanged={s.hasTimeChanged} savingTime={s.savingTime}
          handleSaveTime={s.handleSaveTime} getDayName={s.getDayName}
          allTeams={allTeams} selectedTeamId={s.selectedTeamId}
          setSelectedTeamId={s.setSelectedTeamId} rescheduling={s.rescheduling}
          handleReschedule={s.handleReschedule}
        />

        <SidePanelAttachments
          shift={s.shift} assignedAssets={s.assignedAssets}
          removingEmployee={s.removingEmployee} removingAsset={s.removingAsset}
          handleRemoveEmployee={s.handleRemoveEmployee} handleRemoveAsset={s.handleRemoveAsset}
        />

        <SidePanelComments
          isJobShift={s.isJobShift} crmTaskId={s.crmTaskId} hasTaskContext={!!s.hasTaskContext}
          showComments={s.showComments} setShowComments={s.setShowComments}
          jobComments={s.jobComments} jobAttachments={s.jobAttachments}
          commentsLoading={s.commentsLoading} attachmentsLoading={s.attachmentsLoading}
          newCommentText={s.newCommentText} setNewCommentText={s.setNewCommentText}
          pendingFiles={s.pendingFiles} sendingComment={s.sendingComment}
          deletingCommentId={s.deletingCommentId} deletingAttachmentId={s.deletingAttachmentId}
          attachingToCommentId={s.attachingToCommentId}
          handleSendComment={s.handleSendComment} handleAddPendingFile={s.handleAddPendingFile}
          handleRemovePendingFile={s.handleRemovePendingFile}
          handleAttachToComment={s.handleAttachToComment}
          handleDeleteComment={s.handleDeleteComment} handleDeleteAttachment={s.handleDeleteAttachment}
        />
      </div>

      {/* Footer Actions */}
      <div className={styles.footer}>
        {task && (
          <>
            {s.isDone ? (
              <button onClick={s.handleReopen} disabled={s.changingStatus} className={styles.secondaryButton}>
                Wieder öffnen
              </button>
            ) : (
              <button
                onClick={s.handleMarkDone} disabled={s.changingStatus}
                className={styles.primaryButton}
                style={{ background: s.changingStatus ? '#ccc' : '#10b981' }}
              >
                <FaCheck size={12} />
                {s.changingStatus ? 'Wird gespeichert...' : 'Als erledigt markieren'}
              </button>
            )}
          </>
        )}
        {s.shift && s.shift.is_job_shift && (
          <>
            <button onClick={s.handleUnschedule} disabled={s.unscheduling} className={styles.secondaryButton}>
              <FaUndo size={12} />
              {s.unscheduling ? 'Wird verschoben...' : 'Aus Planung entfernen'}
            </button>
            <button onClick={s.handleDeleteShift} disabled={s.deletingShift} className={styles.dangerButton}>
              <FaTrash size={12} />
              {s.deletingShift ? 'Wird gelöscht...' : 'Job löschen'}
            </button>
          </>
        )}
        {s.shift && !s.shift.is_job_shift && (
          <button onClick={s.handleDeleteShift} disabled={s.deletingShift} className={styles.dangerButton}>
            <FaTrash size={12} />
            {s.deletingShift ? 'Wird gelöscht...' : 'Schicht löschen'}
          </button>
        )}
        {task && !s.shift && (
          <button onClick={s.handleDeleteJob} disabled={s.deletingJob} className={styles.dangerButton}>
            <FaTrash size={12} />
            {s.deletingJob ? 'Wird gelöscht...' : 'Job löschen'}
          </button>
        )}
      </div>

      <PlantafelConflictModal
        isOpen={s.conflictModalOpen} conflictType="workArea"
        workAreaConflicts={s.rescheduleConflicts}
        entityName={s.shift?.crm_task_title || task?.title || 'Job'}
        shiftDate={s.shift ? s.formatMultiDayDateTime(s.shift.start_time, s.shift.end_time) : ''}
        onConfirm={s.closeConflictModal}
        onCancel={s.closeConflictModal}
      />
    </div>
  );
}
